import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { API_BASE_URL } from '../../config/api';
import { toast } from 'sonner';
import { RequiredLabel } from '../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../components/forms/formValidationUtils';

type User = {
    user_id: number;
    username: string;
    full_name: string;
    role: string;
    user_role_id?: number | null;
    role_name?: string | null;
    is_active: boolean;
};

type RoleOption = {
    role_id: number;
    role_name: string;
};

export function UsersManager() {
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<RoleOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [newUser, setNewUser] = useState({
        username: '',
        full_name: '',
        user_role_id: 0,
        password: ''
    });
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [password, setPassword] = useState('');
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [editForm, setEditForm] = useState({
        username: '',
        full_name: '',
        user_role_id: 0,
        is_active: true
    });
    const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
    const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
    const [editErrors, setEditErrors] = useState<Record<string, string>>({});

    const fetchRoles = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/system/roles`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to load roles');
            }
            const data = await res.json();
            setRoles(Array.isArray(data) ? data : []);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load roles');
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/system/users`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to load users');
            }
            setUsers(await res.json());
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
        fetchUsers();
    }, []);

    useEffect(() => {
        if (roles.length > 0 && newUser.user_role_id === 0) {
            setNewUser(prev => ({ ...prev, user_role_id: roles[0].role_id }));
        }
    }, [roles]);

    const handleCreate = async () => {
        const validation = validateRequiredFields([
            { key: 'new_username', label: 'Username', value: newUser.username, required: true },
            { key: 'new_full_name', label: 'Full Name', value: newUser.full_name, required: true },
            { key: 'new_user_role_id', label: 'Role', value: newUser.user_role_id, required: true },
            { key: 'new_password', label: 'Password', value: newUser.password, required: true },
        ]);
        if (!validation.valid) {
            setCreateErrors(validation.errorsByField);
            showValidationSummaryToast();
            focusFirstInvalidField(validation.firstInvalidField);
            return;
        }
        setCreateErrors({});
        try {
            const res = await fetch(`${API_BASE_URL}/system/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUser)
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || 'Failed to create user');
            }
            toast.success('User created');
            setNewUser({ username: '', full_name: '', user_role_id: roles[0]?.role_id || 0, password: '' });
            fetchUsers();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create user');
        }
    };

    const handlePasswordUpdate = async () => {
        if (!selectedUser) return;
        const validation = validateRequiredFields([
            { key: 'password', label: 'New Password', value: password, required: true },
        ]);
        if (!validation.valid) {
            setPasswordErrors(validation.errorsByField);
            showValidationSummaryToast();
            focusFirstInvalidField(validation.firstInvalidField);
            return;
        }
        setPasswordErrors({});
        try {
            const res = await fetch(`${API_BASE_URL}/system/users/${selectedUser.user_id}/password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || 'Failed to update password');
            }
            toast.success(`Password updated for ${selectedUser.username}`);
            setPassword('');
            setSelectedUser(null);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update password');
        }
    };

    const openEdit = (user: User) => {
        setEditingUser(user);
        setEditForm({
            username: user.username,
            full_name: user.full_name,
            user_role_id: Number(user.user_role_id || 0),
            is_active: user.is_active
        });
    };

    const handleUpdateUser = async () => {
        if (!editingUser) return;
        const validation = validateRequiredFields([
            { key: 'edit_username', label: 'Username', value: editForm.username, required: true },
            { key: 'edit_full_name', label: 'Full Name', value: editForm.full_name, required: true },
        ]);
        if (!validation.valid) {
            setEditErrors(validation.errorsByField);
            showValidationSummaryToast();
            focusFirstInvalidField(validation.firstInvalidField);
            return;
        }
        setEditErrors({});
        try {
            const res = await fetch(`${API_BASE_URL}/system/users/${editingUser.user_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: editForm.username,
                    full_name: editForm.full_name,
                    user_role_id: editForm.user_role_id,
                    is_active: editForm.is_active
                })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || 'Failed to update user');
            }
            toast.success('User updated');
            setEditingUser(null);
            fetchUsers();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update user');
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Create User</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                        <RequiredLabel htmlFor="new_username" required className="text-slate-700">Username</RequiredLabel>
                        <Input
                            id="new_username"
                            name="new_username"
                            aria-invalid={!!createErrors.new_username}
                            aria-required="true"
                            value={newUser.username}
                            onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                            placeholder="username"
                        />
                        <FieldErrorText message={createErrors.new_username} />
                    </div>
                    <div className="space-y-2">
                        <RequiredLabel htmlFor="new_full_name" required className="text-slate-700">Full Name</RequiredLabel>
                        <Input
                            id="new_full_name"
                            name="new_full_name"
                            aria-invalid={!!createErrors.new_full_name}
                            aria-required="true"
                            value={newUser.full_name}
                            onChange={e => setNewUser({ ...newUser, full_name: e.target.value })}
                            placeholder="Full name"
                        />
                        <FieldErrorText message={createErrors.new_full_name} />
                    </div>
                    <div className="space-y-2">
                        <RequiredLabel htmlFor="new_user_role_id" required className="text-slate-700">Role</RequiredLabel>
                        <select
                            id="new_user_role_id"
                            name="new_user_role_id"
                            aria-invalid={!!createErrors.new_user_role_id}
                            className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={newUser.user_role_id}
                            onChange={e => setNewUser({ ...newUser, user_role_id: Number(e.target.value) })}
                        >
                            {roles.map(role => (
                                <option key={role.role_id} value={role.role_id}>{role.role_name}</option>
                            ))}
                        </select>
                        <FieldErrorText message={createErrors.new_user_role_id} />
                    </div>
                    <div className="space-y-2">
                        <RequiredLabel htmlFor="new_password" required className="text-slate-700">Password</RequiredLabel>
                        <Input
                            id="new_password"
                            name="new_password"
                            aria-invalid={!!createErrors.new_password}
                            aria-required="true"
                            type="password"
                            value={newUser.password}
                            onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                            placeholder="Strong password"
                        />
                        <FieldErrorText message={createErrors.new_password} />
                        <p className="text-xs text-slate-500">
                            Minimum 8 characters with uppercase, lowercase, number, and symbol.
                        </p>
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                        <Button onClick={handleCreate}>Create User</Button>
                    </div>
                </CardContent>
            </Card>

            <Card className="h-[calc(100vh-340px)] flex flex-col">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Users</CardTitle>
                    <Button variant="outline" onClick={fetchUsers} disabled={loading}>
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                </CardHeader>
                <CardContent className="flex-1 overflow-auto p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0 z-10">
                            <tr>
                                <th className="px-6 py-3 text-left font-medium text-slate-500 border-b">Username</th>
                                <th className="px-6 py-3 text-left font-medium text-slate-500 border-b">Full Name</th>
                                <th className="px-6 py-3 text-left font-medium text-slate-500 border-b">Role</th>
                                <th className="px-6 py-3 text-left font-medium text-slate-500 border-b">Active</th>
                                <th className="px-6 py-3 text-right font-medium text-slate-500 border-b">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {loading ? (
                                <tr><td colSpan={5} className="p-8 text-center text-slate-500">Loading...</td></tr>
                            ) : users.length === 0 ? (
                                <tr><td colSpan={5} className="p-8 text-center text-slate-500">No users found</td></tr>
                            ) : (
                                users.map(user => (
                                    <tr key={user.user_id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-3">{user.username}</td>
                                        <td className="px-6 py-3">{user.full_name}</td>
                                        <td className="px-6 py-3">{user.role_name || user.role}</td>
                                        <td className="px-6 py-3">{user.is_active ? 'Yes' : 'No'}</td>
                                        <td className="px-6 py-3 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEdit(user)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedUser(user);
                                                        setPassword('');
                                                    }}
                                                >
                                                    Set Password
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            {selectedUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4">
                    <Card className="w-full max-w-md shadow-xl">
                        <CardHeader>
                            <CardTitle>Set Password for {selectedUser.username}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <RequiredLabel htmlFor="password" required className="text-slate-700">New Password</RequiredLabel>
                                <Input
                                    id="password"
                                    name="password"
                                    aria-invalid={!!passwordErrors.password}
                                    aria-required="true"
                                    type="password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Enter new password"
                                />
                                <FieldErrorText message={passwordErrors.password} />
                            </div>
                            <div className="flex gap-3">
                                <Button onClick={handlePasswordUpdate}>Update Password</Button>
                                <Button variant="outline" onClick={() => { setSelectedUser(null); setPassword(''); }}>
                                    Cancel
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {editingUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4">
                    <Card className="w-full max-w-md shadow-xl">
                        <CardHeader>
                            <CardTitle>Edit User</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <RequiredLabel htmlFor="edit_username" required className="text-slate-700">Username</RequiredLabel>
                                <Input
                                    id="edit_username"
                                    name="edit_username"
                                    aria-invalid={!!editErrors.edit_username}
                                    aria-required="true"
                                    value={editForm.username}
                                    onChange={e => setEditForm({ ...editForm, username: e.target.value })}
                                />
                                <FieldErrorText message={editErrors.edit_username} />
                            </div>
                            <div className="space-y-2">
                                <RequiredLabel htmlFor="edit_full_name" required className="text-slate-700">Full Name</RequiredLabel>
                                <Input
                                    id="edit_full_name"
                                    name="edit_full_name"
                                    aria-invalid={!!editErrors.edit_full_name}
                                    aria-required="true"
                                    value={editForm.full_name}
                                    onChange={e => setEditForm({ ...editForm, full_name: e.target.value })}
                                />
                                <FieldErrorText message={editErrors.edit_full_name} />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">Role</label>
                                <select
                                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={editForm.user_role_id}
                                    onChange={e => setEditForm({ ...editForm, user_role_id: Number(e.target.value) })}
                                >
                                    {roles.map(role => (
                                        <option key={role.role_id} value={role.role_id}>{role.role_name}</option>
                                    ))}
                                </select>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4"
                                    checked={editForm.is_active}
                                    onChange={e => setEditForm({ ...editForm, is_active: e.target.checked })}
                                />
                                Active
                            </label>
                            <div className="flex gap-3">
                                <Button onClick={handleUpdateUser}>Save Changes</Button>
                                <Button variant="outline" onClick={() => setEditingUser(null)}>
                                    Cancel
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
